import * as React from 'react';
import { FormControl, FormHelperText, InputAdornment, Tooltip, Box, Typography, TextField as MuiTextField, } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { INPUT_TYPOGRAPHY_SX } from './inputTypography';
const TextField = React.forwardRef((props, ref) => {
    var _a, _b, _c, _d, _e;
    const placeholderHasMandatoryMarker = typeof props.placeholder === 'string' && /\*/.test(props.placeholder);
    const isMandatoryField = Boolean(props.required || props.isMandatoryField || placeholderHasMandatoryMarker);
    const [pwdTypeToggle, setPwdTypeToggle] = React.useState(false);
    const [amountVal, setAmountVal] = React.useState((_a = props.value) !== null && _a !== void 0 ? _a : '');
    const [isFocused, setIsFocused] = React.useState(false);
    const [isAutoFilled, setIsAutoFilled] = React.useState(false);
    React.useEffect(() => {
        var _a, _b;
        if (props.IsAmount && !isFocused) {
            const raw = String((_a = props.value) !== null && _a !== void 0 ? _a : '').replace(/[$,]/g, '');
            if (raw !== '' && !isNaN(Number(raw)) && Number(raw) > 0) {
                const parts = raw.split('.');
                const intFormatted = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                const formatted = parts.length > 1
                    ? `$${intFormatted}.${parts[1].slice(0, 2)}`
                    : `$${intFormatted}`;
                setAmountVal(formatted);
            }
            else {
                setAmountVal((_b = props.value) !== null && _b !== void 0 ? _b : '');
            }
        }
    }, [props.value]);
    const sanitizedPlaceholder = (() => {
        if (typeof props.placeholder !== 'string')
            return props.placeholder;
        if (isMandatoryField)
            return props.placeholder.replace(/\s*\*+\s*$/, '');
        return props.placeholder;
    })();
    const handleChange = (e) => {
        let value = e.target.value;
        if (props === null || props === void 0 ? void 0 : props.IsAmount) {
            value = value.replace(/[^0-9.]/g, '');
            if ((value.startsWith('0') || value.startsWith('.')) && !props.IsZeroAllowble) {
                value = value.replace(/^0+|^\./, '');
            }
            if (props.IsZeroAllowble && value.startsWith('0') && value !== '0' && !value.startsWith('0.')) {
                value = value.replace(/^0+/, '');
            }
            const parts = value.split('.');
            let integerPart = parts[0];
            let decimalPart = parts[1];
            if (decimalPart !== undefined) {
                decimalPart = decimalPart.slice(0, 2);
            }
            value = decimalPart !== undefined ? `${integerPart}.${decimalPart}` : integerPart;
            const formattedValue = value !== '' ? `$${value.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : '';
            value = formattedValue;
            setAmountVal(formattedValue);
        }
        else {
            if (props.testRegExp) {
                value = props.testRegExp.test(value) ? value : value.slice(0, -1);
            }
            else if (props.regExp) {
                value = value === null || value === void 0 ? void 0 : value.replace(props.regExp, '');
            }
        }
        props.onChange(Object.assign(Object.assign({}, e), { target: Object.assign(Object.assign({}, e.target), { name: props.name, value }) }));
    };
    const labelNode = props.isOcrPrefilled ? (React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center', gap: 3 } },
        React.createElement(CheckCircleIcon, { style: { fontSize: 15, color: '#2E7D32' } }),
        props.label,
        isMandatoryField && React.createElement("span", { style: { color: '#F00214' } }, " *"),
        props.tooltip && (React.createElement(Tooltip, { title: props.tooltip },
            React.createElement(InfoOutlinedIcon, { sx: { ml: 0.5, fontSize: '16px' } }))))) : isMandatoryField && props.label ? (React.createElement(React.Fragment, null,
        props.label,
        React.createElement("span", { style: { color: '#F00214' } }, " *"),
        props.tooltip && (React.createElement(Tooltip, { title: props.tooltip },
            React.createElement(InfoOutlinedIcon, { sx: { ml: 0.5, fontSize: '16px' } }))))) : (React.createElement(React.Fragment, null,
        props.label,
        props.tooltip && (React.createElement(Tooltip, { title: props.tooltip },
            React.createElement(InfoOutlinedIcon, { sx: { ml: 0.5, fontSize: '16px' } })))));
    const inputType = props.maskValue ? 'password'
        : props.inputType === 'password' ? (pwdTypeToggle ? 'text' : 'password')
            : props.IsAmount ? 'text'
                : props.type;
    const fieldValue = props.IsAmount ? amountVal : ((_b = props.value) !== null && _b !== void 0 ? _b : '');
    const handleAnimationStart = (e) => {
        try {
            setIsAutoFilled(e.target.matches(':-webkit-autofill'));
        }
        catch (_a) { }
    };
    const shouldShrinkLabel = (_d = (_c = props.InputLabelProps) === null || _c === void 0 ? void 0 : _c.shrink) !== null && _d !== void 0 ? _d : (isFocused || Boolean(fieldValue) || fieldValue === 0 || isAutoFilled);
    const shouldShowStartAdornment = Boolean(props.startAdornment) && (isFocused || props.hideLabel || Boolean(fieldValue));
    const formControlNode = (React.createElement(FormControl, { fullWidth: props.fullWidth, error: props.error, sx: props === null || props === void 0 ? void 0 : props.sx },
        React.createElement(MuiTextField, { inputRef: ref, variant: "outlined", size: "small", label: props.label ? labelNode : undefined, name: props.name, id: props.id, value: fieldValue, onChange: handleChange, autoFocus: props.autoFocus, autoComplete: "off", disabled: props.disabled, multiline: props.multiline, maxRows: props.maxRows, minRows: props.minRows, onKeyDown: props.onKeyDown, onPaste: props.onPaste, placeholder: isFocused ? sanitizedPlaceholder : '', type: inputType, error: props.error, fullWidth: props.fullWidth, onFocus: (e) => { var _a; setIsFocused(true); (_a = props.onFocus) === null || _a === void 0 ? void 0 : _a.call(props, e); }, onBlur: (e) => { var _a; setIsFocused(false); (_a = props.onBlur) === null || _a === void 0 ? void 0 : _a.call(props, e); }, className: `${props.className || ''} ${props.disabled ? 'disable-fields' : ''}`, inputProps: Object.assign({ maxLength: props.maxLength ? props.maxLength : 50, required: props.required, onAnimationStart: handleAnimationStart }, (props.inputProps)), InputLabelProps: Object.assign(Object.assign({}, (props.InputLabelProps)), { shrink: shouldShrinkLabel, sx: Object.assign(Object.assign({}, ((_e = props.InputLabelProps) === null || _e === void 0 ? void 0 : _e.sx)), { '&.MuiInputLabel-shrink': {} }) }), InputProps: Object.assign(Object.assign(Object.assign({}, (shouldShowStartAdornment && {
                startAdornment: (React.createElement(InputAdornment, { position: "start" },
                    React.createElement(Typography, null, props.startAdornment))),
            })), (props.inputType === 'password' && {
                endAdornment: (React.createElement(InputAdornment, { position: "end" },
                    React.createElement(Box, { onClick: () => setPwdTypeToggle(!pwdTypeToggle), sx: { cursor: 'pointer', display: 'flex', alignItems: 'center' } }, pwdTypeToggle ? React.createElement(VisibilityOffOutlinedIcon, null) : React.createElement(VisibilityOutlinedIcon, null)))),
            })), (props.inputType !== 'password' && props.endAdornment && {
                endAdornment: (React.createElement(InputAdornment, { position: "end" }, props.endAdornment)),
            })), style: { backgroundColor: '#fff', borderRadius: '4px' }, sx: Object.assign(Object.assign(Object.assign({}, INPUT_TYPOGRAPHY_SX), { '& .MuiInputBase-root.Mui-disabled': {
                    '& .MuiOutlinedInput-notchedOutline': {
                        background: 'rgb(0 0 0 / 5%)',
                    },
                }, '& .MuiInputBase-input': Object.assign(Object.assign({}, INPUT_TYPOGRAPHY_SX['& .MuiInputBase-input']), { backgroundColor: '#fff', borderRadius: '4px' }) }), (props === null || props === void 0 ? void 0 : props.sx)) }),
        React.createElement(FormHelperText, null, props.error_message)));
    return props.disabled && props.disabledReason ? (React.createElement(Tooltip, { title: props.disabledReason, arrow: true, placement: "top" }, formControlNode)) : formControlNode;
});
export default React.memo(TextField);
